import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createDataBucket, updateDataBucket, deleteDataBucket, addDataBucketItem, updateDataBucketItem, deleteDataBucketItem } from '../../../Slices/dataBucket';
import { Form, Spin, notification, Card, Typography, Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faGear, faCode, faUser } from '@fortawesome/free-solid-svg-icons';

import { AntdButton, } from '../../../blueplanitUtils/InputField';
import CreateItemModal from '../../../components/DropdownManager/CreateItemModal';
import DropdownItemList from '../../../components/DropdownManager/DropdownItemList';

const { Title, Text } = Typography;

const NestedDataBucket = () => {
  const dispatch = useDispatch();
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);

  // Form instance
  const [form] = Form.useForm();

  // Developer mode state
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);

  // Enhanced loading states for different operations
  // Enhanced loading states for different operations
  const [loading, setLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(null);
  const [deletingLoading, setDeletingLoading] = useState(null);
  const [copyingLoading, setCopyingLoading] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('page');
  const [selectedParent, setSelectedParent] = useState(null);;
  const [editableNode, setEditableNode] = useState(null);
  const [editingNodeData, setEditingNodeData] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Copy ID to clipboard with loading state
  const copyToClipboard = async (id, type, event) => {
    event.stopPropagation();
    setCopyingLoading(id);
    try {
      await navigator?.clipboard?.writeText(id);
      notification.success({
        message: 'ID Copied',
        description: `${type} ID copied to clipboard: ${id}`,
        duration: 2,
      });
    } catch (error) {

      notification.error({
        message: 'Copy Failed',
        description: 'Failed to copy ID to clipboard',
      });
    } finally {
      setCopyingLoading(null);
    }
  };

  useEffect(() => {
    if (NestedDatabucket?.length) {
      const keys = [];
      const traverse = nodes => {
        nodes.forEach(n => {
          keys.push(n._id);
          if (n.children?.length) traverse(n.children);
        });
      };
      traverse(NestedDatabucket);
      setExpandedKeys(keys);
    }
  }, [NestedDatabucket]);

  // Fixed openModal function
  const openModal = (type, parentId = null, pageId = null) => {
    const actionKey = `create-${type}`;
    if (!isActionAllowed(actionKey)) {
      notification.warning({
        message: 'Access Restricted',
        description: 'Only developers can create pages and dropdowns. Switch to developer mode to access this feature.',
      });
      return;
    }

    setModalType(type);
    setSelectedParent(parentId);

    form.resetFields();
    setIsModalVisible(true);
  };

  // Modal close handler
  const handleCloseModal = () => {
    setSelectedParent(null);
    setIsModalVisible(false);
  };

  // Fixed handleCreateItem function with enhanced loading
  const handleCreateItem = async (values) => {
    setLoading(true);
    try {
      const itemData = {
        ...values,
        bucketLevel: modalType,
        parentId: selectedParent
      };

      const resultAction = await dispatch(createDataBucket(itemData));
      const createdItem = resultAction.payload;

      await dispatch(addDataBucketItem({
        parentId: selectedParent,
        newItem: { ...createdItem, children: [] }
      }));

      handleCloseModal();
      form.resetFields();

      const itemTypeText = modalType === 'page' ? 'Page' : modalType === 'dropdown' ? 'Dropdown' : 'Option';
      notification.success({
        message: 'Success',
        description: `${itemTypeText} created successfully!`,
      });
    } catch (err) {
      notification.error({
        message: 'Error',
        description: `Failed to create the ${modalType}.`,
      });
    } finally {
      setLoading(false);

    }
  };

  // Utility to find a node by id in a nested tree
  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node._id === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Enhanced handleUpdateItem with loading state
  // Enhanced handleUpdateItem with loading state
  const handleUpdateItem = async (nodeId, updatedData) => {
    setEditingLoading(nodeId);
    setEditingLoading(nodeId);
    try {
      const oldNode = findNodeById(NestedDatabucket, nodeId);
      const resultAction = await dispatch(updateDataBucket({ id: nodeId, ...updatedData }));
      const updatedItem = resultAction.payload;
      dispatch(updateDataBucketItem({
        id: nodeId,
        updatedData: {
          ...updatedItem,
          children: oldNode?.children || []
        }
      }));
      notification.success({
        message: 'Success',
        description: 'Item updated successfully!',
      });
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to update the item.',
      });
    } finally {
      setEditingLoading(null);

    }
  };

  // Enhanced handleDeleteItem with loading state
  // Enhanced handleDeleteItem with loading state
  const handleDeleteItem = async (nodeId, itemType) => {
    const actionKey = `delete-${itemType}`;
    if (!isActionAllowed(actionKey)) {
      notification.warning({
        message: 'Access Restricted',
        description: 'Only developers can delete pages and dropdowns. Switch to developer mode to access this feature.',
      });
      return;
    }

    setDeletingLoading(nodeId);
    setDeletingLoading(nodeId);
    try {
      await dispatch(deleteDataBucket(nodeId));
      dispatch(deleteDataBucketItem(nodeId));
      notification.success({
        message: 'Success',
        description: 'Item deleted successfully!',
      });
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to delete the item.',
      });
    } finally {
      setDeletingLoading(null);

    }
  };

  const handleEditNode = (node) => {
    const actionKey = `edit-${node.bucketLevel}`;
    if (!isActionAllowed(actionKey)) {
      notification.warning({
        message: 'Access Restricted',
        description: 'Only developers can edit pages and dropdowns. Switch to developer mode to access this feature.',
      });
      return;
    }

    setEditableNode(node._id);
    setEditingNodeData({
      name: node.name,
      bucketLevel: node.bucketLevel
    });
  };

  const saveNodeEdit = async (node) => {
    if (!editingNodeData.name.trim()) {
      notification.error({
        message: 'Error',
        description: 'Please enter a valid name.',
      });
      return;
    }

    await handleUpdateItem(node._id, editingNodeData);
    setEditableNode(null);
    setEditingNodeData(null);
  };

  const cancelEdit = () => {
    setEditableNode(null);
    setEditingNodeData(null);
  };

  // Permission check function
  const isActionAllowed = (action) => {
    if (isDeveloperMode) return true;
    return (
      action === 'create-option' ||
      action === 'edit-option' ||
      action === 'delete-option'
    );
  };

  // Update the useEffect for click outside handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.ant-tag')) {
        return;
      }
      setExpandedId(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  if (!NestedDatabucket) {
    return (
      <div style={{ padding: '50px', marginTop: '50px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p>Loading dropdown options...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with Developer Mode Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '50px 0px 20px 0px'
      }}>
        <Title level={3}>
          <FontAwesomeIcon icon={faGear} style={{ marginRight: '8px' }} />
          Dropdown Manager
        </Title>

        {/* Developer Mode Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faUser} style={{ color: !isDeveloperMode ? '#1890ff' : '#999' }} />
            <Switch
              checked={isDeveloperMode}
              onChange={(checked) => {
                setIsDeveloperMode(checked);
                if (checked) {
                  notification.success({
                    message: 'Developer Mode Enabled',
                    description: 'You now have full access to create, edit, and delete pages, dropdowns, and options.',
                  });
                } else {
                  notification.info({
                    message: 'User Mode Enabled',
                    description: 'You can now only create and edit options.',
                  });
                }
              }}
              checkedChildren={<FontAwesomeIcon icon={faCode} />}
              unCheckedChildren={<FontAwesomeIcon icon={faUser} />}
            />
            <FontAwesomeIcon icon={faCode} style={{ color: isDeveloperMode ? '#1890ff' : '#999' }} />
          </div>

          <Text style={{
            fontSize: '12px',
            color: isDeveloperMode ? '#1890ff' : '#666',
            fontWeight: isDeveloperMode ? 'bold' : 'normal'
          }}>
            {isDeveloperMode ? 'Developer Mode' : 'User Mode'}
          </Text>
        </div>
      </div>

      {/* Create Page Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <AntdButton
          type="primary"
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => openModal('page')}
          disabled={!isActionAllowed('create-page')}
          label={" Create New Page"}
        />
      </div>

      {/* Use DropdownItemList component for the accordion */}
      <DropdownItemList
        pages={NestedDatabucket?.filter(page => page.bucketLevel === 'page') || []}
        isDeveloperMode={isDeveloperMode}
        isActionAllowed={isActionAllowed}
        expandedKeys={expandedKeys}
        setExpandedKeys={setExpandedKeys}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        copyingLoading={copyingLoading}
        deletingLoading={deletingLoading}
        editingLoading={editingLoading}
        editableNode={editableNode}
        editingNodeData={editingNodeData}
        setEditingNodeData={setEditingNodeData}
        onCopyToClipboard={copyToClipboard}
        onOpenModal={openModal}
        onEditNode={handleEditNode}
        onSaveNodeEdit={saveNodeEdit}
        onCancelEdit={cancelEdit}
        onDeleteItem={handleDeleteItem}
      />

      {/* Use the separated CreateItemModal component */}
      <CreateItemModal
        isVisible={isModalVisible}
        modalType={modalType}
        loading={loading}
        onClose={handleCloseModal}
        onSubmit={handleCreateItem}
        form={form}
      />
    </div>
  );
};

export default NestedDataBucket;